import { useState, useEffect, useRef } from 'react';
import { X, Printer, ZoomIn, ZoomOut, FileText, MapPin, Clock, Package, Truck as TruckIcon, FileImage } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from './ui/dialog';

export const PrintPreviewModal = ({ 
  open, 
  onOpenChange, 
  report, 
  companyInfo,
  loadedDocuments,
  onPrint 
}) => {
  const [zoom, setZoom] = useState(100);
  const previewRef = useRef(null);

  if (!report) return null;

  // Calculate hours
  let totalHours = 0;
  let pauseHours = 0;
  
  if (report.start_time && report.end_time) {
    const start = new Date(`${report.date}T${report.start_time}`);
    const end = new Date(`${report.date}T${report.end_time}`);
    totalHours = (end - start) / (1000 * 60 * 60);
  }
  
  report.stops?.forEach(stop => {
    if (stop.stop_type === 'pause' && stop.arrival_datetime && stop.departure_datetime) {
      const arrival = new Date(stop.arrival_datetime);
      const departure = new Date(stop.departure_datetime);
      pauseHours += (departure - arrival) / (1000 * 60 * 60);
    }
  });
  
  const payableHours = Math.max(0, totalHours - pauseHours);

  const handlePrint = () => {
    onOpenChange(false);
    setTimeout(() => {
      if (onPrint) onPrint();
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[95vh] p-0 overflow-hidden print:hidden">
        <DialogHeader className="p-4 border-b border-slate-800 flex-row items-center justify-between">
          <DialogTitle className="text-white text-lg flex items-center gap-2">
            <FileText className="text-amber-500" size={20} />
            Aperçu avant impression
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.max(50, zoom - 10))}
              className="h-8 w-8 p-0 border-slate-700"
            >
              <ZoomOut size={16} />
            </Button>
            <span className="text-slate-400 text-sm w-12 text-center">{zoom}%</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.min(150, zoom + 10))}
              className="h-8 w-8 p-0 border-slate-700"
            >
              <ZoomIn size={16} />
            </Button>
          </div>
        </DialogHeader>
        
        {/* Preview Area with white background simulating print */}
        <div className="overflow-auto p-4 bg-slate-950 max-h-[calc(95vh-140px)]">
          <div 
            ref={previewRef}
            className="mx-auto shadow-2xl transition-transform duration-200"
            style={{ 
              width: `${8.5 * zoom / 100 * 96}px`,
              minHeight: `${11 * zoom / 100 * 96}px`,
              transform: `scale(1)`,
              transformOrigin: 'top center'
            }}
          >
            {/* Simulated Print Page */}
            <div className="bg-white text-black p-8 font-sans" style={{ fontSize: `${12 * zoom / 100}pt` }}>
              {/* Header */}
              <div className="border-b-2 border-gray-300 pb-4 mb-4">
                <div className="flex items-start gap-4">
                  {companyInfo?.logo_base64 ? (
                    <img 
                      src={companyInfo.logo_base64} 
                      alt="Logo" 
                      className="w-16 h-16 object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                      Logo
                    </div>
                  )}
                  <div className="flex-1">
                    <h1 className="text-xl font-bold text-black">{companyInfo?.name || 'Nom entreprise'}</h1>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{companyInfo?.address || 'Adresse'}</p>
                    <p className="text-sm text-gray-600">
                      {companyInfo?.phone} {companyInfo?.email && `• ${companyInfo.email}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Report Title */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold uppercase tracking-wider text-black">Rapport Journalier</h2>
                <p className="text-lg font-semibold text-amber-600">{report.report_number}</p>
              </div>

              {/* Driver & Date Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="border border-gray-300 p-3 rounded">
                  <p className="text-xs text-gray-500 uppercase font-bold">Chauffeur</p>
                  <p className="text-base font-semibold text-black">{report.driver_name}</p>
                </div>
                <div className="border border-gray-300 p-3 rounded">
                  <p className="text-xs text-gray-500 uppercase font-bold">Date</p>
                  <p className="text-base font-semibold text-black">{report.date}</p>
                </div>
              </div>

              {/* Subcontractor if present */}
              {report.subcontractor && (
                <div className="border border-gray-300 p-3 rounded mb-4">
                  <p className="text-xs text-gray-500 uppercase font-bold">Sous-traitant</p>
                  <p className="text-base font-semibold text-black">{report.subcontractor}</p>
                </div>
              )}

              {/* Hours Summary */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="border border-gray-300 p-3 rounded text-center">
                  <p className="text-xs text-gray-500 uppercase font-bold">Début</p>
                  <p className="text-lg font-bold text-black">{report.start_time || '--:--'}</p>
                </div>
                <div className="border border-gray-300 p-3 rounded text-center">
                  <p className="text-xs text-gray-500 uppercase font-bold">Fin</p>
                  <p className="text-lg font-bold text-black">{report.end_time || '--:--'}</p>
                </div>
                <div className="border border-gray-300 p-3 rounded text-center">
                  <p className="text-xs text-gray-500 uppercase font-bold">Pauses</p>
                  <p className="text-lg font-bold text-red-600">-{pauseHours.toFixed(1)}h</p>
                </div>
                <div className="border-2 border-amber-500 bg-amber-50 p-3 rounded text-center">
                  <p className="text-xs text-gray-500 uppercase font-bold">Payable</p>
                  <p className="text-lg font-bold text-amber-600">{payableHours.toFixed(1)}h</p>
                </div>
              </div>

              {/* Distance */}
              <div className="border border-gray-300 p-3 rounded mb-6 text-center">
                <p className="text-xs text-gray-500 uppercase font-bold">Distance parcourue</p>
                <p className="text-xl font-bold text-black">{report.total_km ? `${report.total_km} km` : '-- km'}</p>
              </div>

              {/* Stops Section */}
              <div className="mb-4">
                <h3 className="text-lg font-bold uppercase tracking-wider border-b-2 border-gray-300 pb-2 mb-4 text-black">
                  Arrêts ({report.stops?.length || 0})
                </h3>
                
                {report.stops?.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucun arrêt enregistré</p>
                ) : (
                  <div className="space-y-3">
                    {report.stops?.sort((a, b) => a.order - b.order).map((stop, index) => (
                      <div 
                        key={stop.id} 
                        className="border border-gray-300 rounded p-3"
                        style={{ pageBreakInside: 'avoid' }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-black">{stop.client_name}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded border ${
                                stop.stop_type === 'pickup' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                                stop.stop_type === 'pause' ? 'bg-orange-50 border-orange-300 text-orange-700' :
                                'bg-green-50 border-green-300 text-green-700'
                              }`}>
                                {stop.stop_type === 'pickup' ? 'Cueillette' : 
                                 stop.stop_type === 'pause' ? 'Pause' : 'Livraison'}
                              </span>
                            </div>
                            
                            {/* Pause type */}
                            {stop.stop_type === 'pause' && stop.pause_type && (
                              <p className="text-sm text-orange-600 mb-1">
                                {stop.pause_type === 'lunch' ? 'Dîner' :
                                 stop.pause_type === 'dinner' ? 'Souper' :
                                 stop.pause_type === 'break' ? 'Pause café' :
                                 stop.pause_type === 'overnight' ? 'Nuit / Repos' : 'Autre'}
                                {stop.arrival_datetime && stop.departure_datetime && (() => {
                                  const arrival = new Date(stop.arrival_datetime);
                                  const departure = new Date(stop.departure_datetime);
                                  const duration = (departure - arrival) / (1000 * 60 * 60);
                                  return ` (${duration.toFixed(1)}h)`;
                                })()}
                              </p>
                            )}
                            
                            {stop.address && (
                              <p className="text-sm text-gray-600 mb-1">
                                <MapPin size={12} className="inline mr-1" />
                                {stop.address}
                              </p>
                            )}
                            
                            {/* Delivery notes */}
                            {stop.stop_type !== 'pause' && stop.delivery_note_numbers?.length > 0 && (
                              <p className="text-sm text-gray-700 mb-1">
                                <strong>Bons:</strong> {stop.delivery_note_numbers.join(', ')}
                              </p>
                            )}
                            
                            {/* Quantities */}
                            {stop.stop_type !== 'pause' && (
                              <div className="flex flex-wrap gap-2 text-xs mb-1">
                                {stop.quantities?.palettes > 0 && (
                                  <span className="bg-gray-100 px-2 py-0.5 rounded">{stop.quantities.palettes} palettes</span>
                                )}
                                {stop.quantities?.units > 0 && (
                                  <span className="bg-gray-100 px-2 py-0.5 rounded">{stop.quantities.units} unités</span>
                                )}
                                {stop.quantities?.packages > 0 && (
                                  <span className="bg-gray-100 px-2 py-0.5 rounded">{stop.quantities.packages} paquets</span>
                                )}
                                {stop.quantities?.weight_kg && (
                                  <span className="bg-gray-100 px-2 py-0.5 rounded">{stop.quantities.weight_kg} kg</span>
                                )}
                              </div>
                            )}
                            
                            {/* Times */}
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                              {stop.stop_type !== 'pause' && stop.equipment_used && stop.equipment_used !== 'none' && (
                                <span>
                                  <TruckIcon size={12} className="inline mr-1" />
                                  {stop.equipment_used === 'moffett' ? 'Moffett' : 'Transpalette'}
                                </span>
                              )}
                              {stop.arrival_datetime && (
                                <span>
                                  <Clock size={12} className="inline mr-1" />
                                  Arrivée: {stop.arrival_datetime}
                                </span>
                              )}
                              {stop.departure_datetime && (
                                <span>Départ: {stop.departure_datetime}</span>
                              )}
                            </div>
                            
                            {stop.notes && (
                              <p className="text-sm text-gray-500 mt-1 italic">{stop.notes}</p>
                            )}
                            
                            {stop.scanned_documents?.length > 0 && (
                              <div className="mt-1">
                                <p className="text-xs text-gray-500">
                                  <FileImage size={12} className="inline mr-1" />
                                  {stop.scanned_documents.length} document(s) scanné(s)
                                </p>
                                {stop.document_details?.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {stop.document_details.map((doc, idx) => {
                                      const rawUrl = doc.thumb_url || doc.image_url || '';
                                      const imgSrc = rawUrl.startsWith('data:') ? rawUrl : (rawUrl ? `${process.env.REACT_APP_BACKEND_URL}${rawUrl}` : '');
                                      return (
                                        <img
                                          key={doc.id || idx}
                                          src={imgSrc}
                                          alt={doc.filename || `Scan ${idx + 1}`}
                                          className="w-16 h-16 object-cover rounded border border-gray-300"
                                        />
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t-2 border-gray-300 pt-4 mt-6 text-center text-xs text-gray-500">
                <p>Rapport généré le {new Date().toLocaleString('fr-CA')}</p>
                <p className="mt-1">Statut: {report.status === 'completed' ? 'TERMINÉ' : 'BROUILLON'}</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-slate-800 flex-row gap-2">
          <DialogClose asChild>
            <Button variant="outline" className="border-slate-700 flex-1">
              <X size={16} className="mr-2" />
              Fermer
            </Button>
          </DialogClose>
          <Button 
            onClick={handlePrint}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold flex-1"
          >
            <Printer size={16} className="mr-2" />
            Imprimer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintPreviewModal;
